/**
 * VAT Context
 * Provides global VAT settings and calculation functions to all components
 */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiGet } from '../lib/api';
import { auth } from '../lib/auth';
import {
  calculateVAT,
  calculateGrossAmount,
  calculateNetAmount,
  getVATBreakdown,
  formatVATBreakdown
} from '../lib/vatCalculations';

interface VATSetting {
  id?: number;
  country: string;
  vat_rate: number;
  is_default: number;
}

interface VATContextType {
  defaultVATRate: number;
  defaultCountry: string;
  allSettings: VATSetting[];
  loading: boolean;
  error: string | null;
  calculateVAT: (netAmount: number, vatRate?: number) => number;
  calculateGrossAmount: (netAmount: number, vatRate?: number) => number;
  calculateNetAmount: (grossAmount: number, vatRate?: number) => number;
  getVATBreakdown: (amount: number, vatRate?: number, isGross?: boolean) => { net: number; vat: number; gross: number };
  formatVATBreakdown: (amount: number, vatRate?: number, isGross?: boolean, formatFn?: (n: number) => string) => string;
  getVATRateForCountry: (country: string) => number;
  refreshVATSettings: () => Promise<void>;
}

const VATContext = createContext<VATContextType | undefined>(undefined);

export function VATProvider({ children }: { children: ReactNode }) {
  const [defaultVATRate, setDefaultVATRate] = useState(19); // Default to Germany
  const [defaultCountry, setDefaultCountry] = useState('DE');
  const [allSettings, setAllSettings] = useState<VATSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadVATSettings();
  }, []);

  const loadVATSettings = async () => {
    try {
      setLoading(true);
       setError(null);

       // Only load from API if authenticated
       if (!auth.getToken()) {
         setDefaultVATRate(19);
         setDefaultCountry('DE');
         setLoading(false);
         return;
       }

        // Get VAT settings from API (returns array of settings)
        const result = await apiGet<any>('/vat-settings');
        if (result.success && Array.isArray(result.data)) {
          const settings = result.data as VATSetting[];
          setAllSettings(settings);
          const defaultSetting = settings.find(s => s.is_default === 1) || settings[0];
          if (defaultSetting) {
            setDefaultVATRate(typeof defaultSetting.vat_rate === 'string' ? parseFloat(defaultSetting.vat_rate) : (defaultSetting.vat_rate || 19));
            setDefaultCountry(defaultSetting.country || 'DE');
          } else {
            setDefaultVATRate(19);
            setDefaultCountry('DE');
          }
        } else {
          setDefaultVATRate(19);
          setDefaultCountry('DE');
        }
     } catch (err: any) {
       console.error('Error loading VAT settings:', err);
       setError(err.message);
       // Use defaults if loading fails
       setDefaultVATRate(19);
       setDefaultCountry('DE');
    } finally {
      setLoading(false);
    }
  };

  const getVATRateForCountry = (country: string): number => {
    const setting = allSettings.find(s => s.country === country);
    return setting ? setting.vat_rate : defaultVATRate;
  };

  const value: VATContextType = {
    defaultVATRate,
    defaultCountry,
    allSettings,
    loading,
    error,
    calculateVAT: (netAmount, vatRate) => calculateVAT(netAmount, vatRate ?? defaultVATRate),
    calculateGrossAmount: (netAmount, vatRate) => calculateGrossAmount(netAmount, vatRate ?? defaultVATRate),
    calculateNetAmount: (grossAmount, vatRate) => calculateNetAmount(grossAmount, vatRate ?? defaultVATRate),
    getVATBreakdown: (amount, vatRate, isGross) => getVATBreakdown(amount, vatRate ?? defaultVATRate, isGross),
    formatVATBreakdown: (amount, vatRate, isGross, formatFn) => formatVATBreakdown(amount, vatRate ?? defaultVATRate, isGross, formatFn),
    getVATRateForCountry,
    refreshVATSettings: loadVATSettings
  };

  return (
    <VATContext.Provider value={value}>
      {children}
    </VATContext.Provider>
  );
}

export function useVAT(): VATContextType {
  const context = useContext(VATContext);
  if (context === undefined) {
    throw new Error('useVAT must be used within a VATProvider');
  }
  return context;
}
